import React, { type CSSProperties, type FC } from 'react'
import type { SxProps, Theme } from '@mui/material'

import { type ColumnView, columnWidths, setColumnWidth } from '../../../../domain'

import { TABLE_CHECKBOX_COLUMN_WIDTH } from '../../theme'
import { TRAILING_COLUMN_WIDTH } from './RowActions'
import { useAllColumnNames, useColumnNames, useColumns, useColumnView, useHasColumnView, useHasSelection, useResizingColumn } from './State'
import type { Column, ColumnType } from './Types'

const DEFAULT_COLUMN_MIN_WIDTH = 100
const COLUMN_TYPE_MIN_WIDTHS: Readonly<Record<ColumnType, number>> = { number: 80, datetime: 140 }

export const columnMinWidth = ({ type }: Pick<Column<unknown, unknown>, 'type'>): number => (type ? COLUMN_TYPE_MIN_WIDTHS[type] : DEFAULT_COLUMN_MIN_WIDTH)

type ColumnLayout = Readonly<{
    /** One per rendered cell column, in order; `null` leaves the column a share of the remaining space */
    widths: readonly (number | null)[]
    /** Below it the table scrolls inside its container instead of squeezing columns under their minimum */
    minWidth: number
}>

/** The column view with the width of the column being resized, if any, as it will be once committed */
const useRenderedColumnView = (): ColumnView => {
    const allNames = useAllColumnNames()
    const view = useColumnView()
    const resizing = useResizingColumn()

    return resizing ? setColumnWidth(allNames, view, resizing.name, resizing.width, resizing.minWidth) : view
}

/** `null` for a table without a column view, which keeps the browser's automatic layout */
const useColumnLayout = (): ColumnLayout | null => {
    const hasColumnView = useHasColumnView()
    const hasSelection = useHasSelection()
    const names = useColumnNames()
    const columns = useColumns()
    const view = useRenderedColumnView()

    if (!hasColumnView) {
        return null
    }

    const sized = columnWidths(names, view)
    const columnsWidths = names.map((name, index) => {
        const minWidth = columnMinWidth(columns[name]!)
        const stored = sized[name]
        const width = stored === undefined ? null : Math.max(stored, minWidth)
        /**
         * A fixed layout at `width: 100%` spreads any spare space over every sized column once none is left unsized,
         * so the last one always stays unsized to absorb it, its stored width still setting its floor
         */
        return index === names.length - 1 ? { width: null, minWidth: width ?? minWidth } : { width, minWidth }
    })

    const sizedWidth = columnsWidths.reduce((sum, { width }) => sum + (width ?? 0), 0)
    const unsizedMinWidths = columnsWidths.filter(({ width }) => width === null).map(({ minWidth }) => minWidth)
    /** Fixed layout gives the unsized columns equal shares, so each needs the largest of their minimums for none to fall below its own */
    const unsizedWidth = unsizedMinWidths.length * Math.max(0, ...unsizedMinWidths)
    const checkboxWidth = hasSelection ? TABLE_CHECKBOX_COLUMN_WIDTH : 0

    return {
        widths: [...(hasSelection ? [TABLE_CHECKBOX_COLUMN_WIDTH] : []), ...columnsWidths.map(({ width }) => width), TRAILING_COLUMN_WIDTH],
        minWidth: checkboxWidth + sizedWidth + unsizedWidth + TRAILING_COLUMN_WIDTH,
    }
}

const fixedLayoutStyle: SxProps<Theme> = { tableLayout: 'fixed', width: '100%' }

/**
 * The styling of the `<table>` itself: a fixed layout for a table with a column view, none otherwise.
 * The minimum width changes on every resize drag move, so it's an inline style rather than a new emotion class each time
 */
export const useTableLayout = (): Readonly<{ sx: SxProps<Theme>, style: CSSProperties }> | null => {
    const layout = useColumnLayout()
    return layout ? { sx: fixedLayoutStyle, style: { minWidth: layout.minWidth } } : null
}

export const ColumnGroup: FC = () => {
    const layout = useColumnLayout()

    if (!layout) {
        return null
    }

    return (
        <colgroup>
            {layout.widths.map((width, index) => <col key={index} style={width === null ? undefined : { width }} />)}
        </colgroup>
    )
}
